/**
 * How many looms "1-12, N01-N44" adds, counted the way the server expands it (LoomEntryRules), so
 * the button can say "Add 56 looms". Null when a range is backwards or too long (the server refuses).
 */
const RANGE = /^([A-Za-z]*)(\d+)\s*-\s*([A-Za-z]*)(\d+)$/;

export const countLoomCodes = (raw: string): number | null => {
  const codes = new Set<string>();
  for (const part of raw.split(/[,\n]/)) {
    const p = part.trim();
    if (!p) continue;
    const m = p.match(RANGE);
    if (m && (!m[3] || m[3].toUpperCase() === m[1].toUpperCase())) {
      const a = Number(m[2]);
      const b = Number(m[4]);
      if (b < a || b - a > 500) return null;
      for (let i = a; i <= b; i++) codes.add(`${m[1].toUpperCase()}${String(i).padStart(m[2].length, "0")}`);
    } else {
      codes.add(p.toUpperCase().replace(/\s+/g, ""));
    }
  }
  return codes.size;
};

/**
 * The next few looms after the ones a studio has, to suggest when adding more: N01–N44 → "N45-N49",
 * 1–12 → "13-17". Follows the largest group of codes (same letters), keeping its zero padding.
 */
export const nextLoomRange = (existing: string[], count = 5): string => {
  const groups = new Map<string, { max: number; width: number; n: number }>();
  for (const raw of existing) {
    const m = String(raw).trim().match(/^([A-Za-z]*)(\d+)$/);
    if (!m) continue;
    const key = m[1].toUpperCase();
    const g = groups.get(key) || { max: 0, width: 0, n: 0 };
    g.max = Math.max(g.max, Number(m[2]));
    g.width = Math.max(g.width, m[2].length > 1 && m[2].startsWith("0") ? m[2].length : 0);
    g.n += 1;
    groups.set(key, g);
  }
  let best: [string, { max: number; width: number; n: number }] | null = null;
  groups.forEach((g, k) => {
    if (!best || g.n > best[1].n) best = [k, g];
  });
  if (!best) return "1-12";
  const [prefix, g] = best as [string, { max: number; width: number; n: number }];
  const code = (i: number) => `${prefix}${String(i).padStart(g.width, "0")}`;
  return `${code(g.max + 1)}-${code(g.max + count)}`;
};

/**
 * One key per loom however it is typed: N01 = N1 = n 1, B05 = B5, 007 = 7. Case, spaces, "_" and
 * "-" are ignored, and leading zeros of each number are dropped (a lone 0 stays).
 */
export const loomKey = (code: unknown): string =>
  String(code ?? "")
    .toUpperCase()
    .replace(/[\s_-]+/g, "")
    .replace(/(^|\D)0+(?=\d)/g, "$1");

/** Search: does loom `code` match what was typed (part of it, zeros or not)? Empty matches all. */
export const loomCodeMatches = (code: unknown, typed: string): boolean => {
  const q = loomKey(typed);
  return !q || loomKey(code).includes(q);
};

/** A loom matches by its code or any code it had before a rename (N10 still finds the loom now called N45). */
export const loomMatches = (loom: { code?: unknown; previousCodes?: unknown[] | null } | null | undefined, typed: string): boolean =>
  !!loom && [loom.code, ...(loom.previousCodes || [])].some((c) => loomCodeMatches(c, typed));
